import React, { useEffect, useMemo, useState } from "react";
import Input from "../common/Input";
import Dropdown from "../common/Dropdown";
import Button from "../common/Button";
import {
  TenantLiveWidgets as TenantLiveWidgetsValue,
  WidgetPlacement,
  LiveScoreSport,
  BandWidgetDurations,
  BandWidgetKind,
  ALLOWED_BAND_HEIGHT_PCTS,
  ALLOWED_WIDGET_SECONDS,
  BAND_WIDGET_ORDER,
  DEFAULT_MARKET_SYMBOLS,
  MAX_MARKET_SYMBOLS,
  NEWS_CATEGORIES,
  NewsCategory,
  normalizeLiveBand,
  isValidFeedUrl,
  marketSymbolsError,
  parseMarketSymbols,
  remainingHeightPx,
} from "../../schemas/tenantConfigurationSchema";

/**
 * Tenant-level Live Band: one always-on band applied to every screen the
 * tenant owns. It reserves part of the screen (top or bottom) and the creative
 * is shrunk into the rest. The band is full width and shows one widget at a
 * time — weather, live score, market, news — each for its own duration.
 */

interface TenantLiveWidgetsProps {
  value?: TenantLiveWidgetsValue | null;
  onSave: (next: TenantLiveWidgetsValue) => Promise<void> | void;
  isSaving?: boolean;
}

const PLACEMENT_OPTIONS = [
  { value: "top", label: "Top of screen" },
  { value: "bottom", label: "Bottom of screen" },
];

const HEIGHT_OPTIONS = ALLOWED_BAND_HEIGHT_PCTS.map((pct) => ({
  value: String(pct),
  label: `${pct}% of screen height`,
}));

const SPORT_OPTIONS = [
  { value: "cricket", label: "Cricket" },
  { value: "football", label: "Football / Soccer" },
];

const NEWS_CATEGORY_OPTIONS = NEWS_CATEGORIES.map((category) => ({
  value: category,
  label:
    category === "top"
      ? "Top stories"
      : category.charAt(0).toUpperCase() + category.slice(1),
}));

const WIDGET_INFO: Record<BandWidgetKind, { label: string; hint: string }> = {
  weather: {
    label: "Weather",
    hint: "Local time, conditions, next hours and air quality for each screen's city.",
  },
  livescore: {
    label: "Live score",
    hint: "Skipped while there is no match to show.",
  },
  market: {
    label: "Share market",
    hint: "Index prices and day change.",
  },
  news: {
    label: "News",
    hint: "Latest headlines, with publisher and time.",
  },
};

const TenantLiveWidgets: React.FC<TenantLiveWidgetsProps> = ({
  value,
  onSave,
  isSaving = false,
}) => {
  const initial = useMemo(() => normalizeLiveBand(value), [value]);
  // Resync when the loaded value actually changes (initial load, refetch after
  // save), not on every new object identity from the query.
  const initialKey = JSON.stringify(initial);

  const [enabled, setEnabled] = useState(initial.enabled);
  const [placement, setPlacement] = useState<WidgetPlacement>(initial.placement);
  const [heightPct, setHeightPct] = useState(initial.heightPct);
  const [widgets, setWidgets] = useState<BandWidgetKind[]>(initial.widgets);
  const [durations, setDurations] = useState<BandWidgetDurations>(
    initial.durations
  );
  const [sport, setSport] = useState<LiveScoreSport>(initial.livescore.sport);
  const [matchId, setMatchId] = useState(initial.livescore.matchId ?? "");
  const [feedUrl, setFeedUrl] = useState(initial.news.feedUrl ?? "");
  const [newsCategory, setNewsCategory] = useState<NewsCategory>(
    initial.news.category
  );
  const [marketSymbols, setMarketSymbols] = useState(
    (initial.market.symbols ?? []).join(", ")
  );

  useEffect(() => {
    setEnabled(initial.enabled);
    setPlacement(initial.placement);
    setHeightPct(initial.heightPct);
    setWidgets(initial.widgets);
    setDurations(initial.durations);
    setSport(initial.livescore.sport);
    setMatchId(initial.livescore.matchId ?? "");
    setFeedUrl(initial.news.feedUrl ?? "");
    setNewsCategory(initial.news.category);
    setMarketSymbols((initial.market.symbols ?? []).join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  const isOn = (kind: BandWidgetKind) => widgets.includes(kind);
  const toggleWidget = (kind: BandWidgetKind, on: boolean) =>
    setWidgets((prev) =>
      // Kept in rotation order, whatever order they were ticked in.
      BAND_WIDGET_ORDER.filter((k) => (k === kind ? on : prev.includes(k)))
    );

  const feedUrlValid = isValidFeedUrl(feedUrl);
  const symbolsError = marketSymbolsError(marketSymbols);
  const noWidgets = widgets.length === 0;
  // Only fields of switched-on widgets block saving; the backend drops
  // invalid settings of widgets that are off.
  const canSave =
    !noWidgets &&
    (!isOn("news") || feedUrlValid) &&
    (!isOn("market") || symbolsError === null);

  const handleSave = () => {
    if (!canSave) return;
    const symbols = parseMarketSymbols(marketSymbols);
    onSave({
      band: {
        enabled,
        placement,
        heightPct,
        widgets,
        durations,
        livescore: { sport, matchId: matchId.trim() || null },
        news: { feedUrl: feedUrl.trim() || null, category: newsCategory },
        market: { symbols: symbols.length ? symbols : null },
      },
    });
  };

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      <h3 className="text-lg font-semibold text-gray-800">Live Band</h3>
      <p className="text-sm text-gray-500">
        A full-width band shown on all of this tenant&apos;s screens. It
        reserves part of the screen and creatives are shrunk to fit the
        remaining area. The band shows one widget at a time and rotates through
        the widgets switched on below, each for as long as you set.
      </p>

      <div className="mt-4 space-y-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Enabled
        </label>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-2 space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Dropdown
                label="Placement"
                options={PLACEMENT_OPTIONS}
                value={placement}
                onChange={(val) => setPlacement(val as WidgetPlacement)}
              />
              <Dropdown
                label="Band height"
                options={HEIGHT_OPTIONS}
                value={String(heightPct)}
                onChange={(val) => setHeightPct(Number(val))}
              />
            </div>
            <p className="text-xs text-gray-500">
              Author creatives for the remaining area: at 1920×1080 a{" "}
              {heightPct}% band leaves 1920×{remainingHeightPx(1080, heightPct)}
              ; on a portrait 1080×1920 screen it leaves 1080×
              {remainingHeightPx(1920, heightPct)}.
            </p>

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-gray-700">
                Widgets (shown in this order)
              </legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {BAND_WIDGET_ORDER.map((kind) => (
                  <div
                    key={kind}
                    className="flex items-start justify-between gap-2 rounded-md border border-gray-200 p-2 text-sm"
                  >
                    <label className="flex flex-1 items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-gray-300"
                        checked={isOn(kind)}
                        onChange={(e) => toggleWidget(kind, e.target.checked)}
                      />
                      <span>
                        <span className="block font-medium text-gray-700">
                          {WIDGET_INFO[kind].label}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {WIDGET_INFO[kind].hint}
                        </span>
                      </span>
                    </label>
                    <select
                      aria-label={`${WIDGET_INFO[kind].label} duration`}
                      className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                      value={durations[kind]}
                      disabled={!isOn(kind)}
                      onChange={(e) =>
                        setDurations((prev) => ({
                          ...prev,
                          [kind]: Number(e.target.value),
                        }))
                      }
                    >
                      {ALLOWED_WIDGET_SECONDS.map((seconds) => (
                        <option key={seconds} value={seconds}>
                          {seconds}s
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {noWidgets && (
                <p className="mt-1 text-xs text-danger-default">
                  Switch on at least one widget.
                </p>
              )}
            </fieldset>

            {isOn("livescore") && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  Live score
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Dropdown
                    label="Sport"
                    options={SPORT_OPTIONS}
                    value={sport}
                    onChange={(val) => setSport(val as LiveScoreSport)}
                  />
                  <div>
                    <Input
                      label="Match ID (optional)"
                      placeholder="First live match"
                      name="matchId"
                      value={matchId}
                      onChange={(e) => setMatchId(e.target.value)}
                    />
                    <p className="text-xs text-gray-500">
                      Leave blank to show the first live match.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isOn("market") && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  Share market
                </h4>
                <Input
                  label="Symbols (optional)"
                  placeholder={`Default: ${DEFAULT_MARKET_SYMBOLS.join(", ")}`}
                  name="marketSymbols"
                  value={marketSymbols}
                  onChange={(e) => setMarketSymbols(e.target.value)}
                  error={
                    symbolsError
                      ? { type: "validate", message: symbolsError }
                      : undefined
                  }
                />
                <p className="text-xs text-gray-500">
                  Up to {MAX_MARKET_SYMBOLS} Yahoo Finance tickers, comma
                  separated (e.g. ^NSEI for NIFTY 50, ^BSESN for SENSEX, ^IXIC
                  for NASDAQ, GC=F for gold, INR=X for USD/INR). Screens show
                  as many as fit: about 4 on portrait, all 6 on landscape.
                  Leave blank for the default. Test data source only.
                </p>
              </div>
            )}

            {isOn("news") && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">News</h4>
                <Dropdown
                  label="Category"
                  options={NEWS_CATEGORY_OPTIONS}
                  value={newsCategory}
                  onChange={(val) => setNewsCategory(val as NewsCategory)}
                />
                <Input
                  label="Custom RSS feed URL (optional)"
                  placeholder="Leave blank to use the category above"
                  name="feedUrl"
                  value={feedUrl}
                  onChange={(e) => setFeedUrl(e.target.value)}
                  error={
                    feedUrlValid
                      ? undefined
                      : {
                          type: "validate",
                          message: "Enter a valid http(s) RSS/Atom URL",
                        }
                  }
                />
                <p className="text-xs text-gray-500">
                  Indian English headlines from NewsData.io in the chosen
                  category. An RSS or Atom URL here replaces them, and the
                  category is then ignored.
                </p>
              </div>
            )}
          </div>

          <BandPreview
            enabled={enabled}
            placement={placement}
            heightPct={heightPct}
            widgets={widgets}
            durations={durations}
          />
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            disabled={!canSave}
          >
            Save Live Band
          </Button>
        </div>
      </div>
    </div>
  );
};

// The preview plays the rotation at 10× speed: a 60s widget holds for 6s.
const PREVIEW_MS_PER_SECOND = 100;

/** "1m 15s" / "45s". */
const formatSeconds = (total: number) => {
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (!minutes) return `${seconds}s`;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
};

/** Proportional 16:9 preview; the band steps through the enabled widgets. */
const BandPreview: React.FC<{
  enabled: boolean;
  placement: WidgetPlacement;
  heightPct: number;
  widgets: BandWidgetKind[];
  durations: BandWidgetDurations;
}> = ({ enabled, placement, heightPct, widgets, durations }) => {
  const [step, setStep] = useState(0);

  const current = widgets.length ? widgets[step % widgets.length] : null;
  const currentMs = current ? durations[current] * PREVIEW_MS_PER_SECOND : 0;

  useEffect(() => {
    if (widgets.length < 2) return;
    const timer = setTimeout(() => setStep((s) => s + 1), currentMs);
    return () => clearTimeout(timer);
  }, [step, widgets.length, currentMs]);

  const cycleSeconds = widgets.reduce((sum, kind) => sum + durations[kind], 0);

  return (
    <div>
      <span className="block mb-1 text-sm font-medium text-gray-700">
        Preview
      </span>
      <div
        className={`relative w-full aspect-video overflow-hidden rounded-md border border-gray-300 flex ${
          placement === "top" ? "flex-col" : "flex-col-reverse"
        } ${enabled ? "" : "opacity-50"}`}
      >
        <div
          className="flex w-full items-center justify-center bg-[#0B1733] text-[10px] text-white"
          style={{ height: `${heightPct}%` }}
        >
          {current ? WIDGET_INFO[current].label : "No widgets"}
        </div>
        <div className="flex flex-1 items-center justify-center bg-info-light text-xs text-gray-600">
          Creative
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {widgets.length
          ? `${widgets
              .map((k) => `${WIDGET_INFO[k].label} ${durations[k]}s`)
              .join(" → ")}. Full cycle ${formatSeconds(cycleSeconds)}.`
          : "No widgets switched on."}
      </p>
      {!enabled && (
        <p className="mt-1 text-xs text-gray-500">
          Band is disabled; settings are kept.
        </p>
      )}
    </div>
  );
};

export default TenantLiveWidgets;
